import { c as _c } from "react/compiler-runtime";
import { isEqual } from 'lodash';
import { useCallback, useEffect, useReducer, useRef, DependencyList, EffectCallback } from 'react';
const useDeepCompareMemoize = (value: DependencyList) => {
  const ref = useRef<DependencyList | null>(null);
  const signalRef = useRef<number>(0);

  /* eslint-disable react-hooks/refs -- Intentional ref reads/writes during render for deep-compare memoization signal */
  if (!isEqual(value, ref.current)) {
    ref.current = value;
    signalRef.current += 1;
  }
  return [signalRef.current];
  /* eslint-enable react-hooks/refs */
};
export function useSetState(initialState) {
  const $ = _c(2);
  const [state, setState] = useReducer(_temp, initialState);
  let t0;
  if ($[0] !== state) {
    t0 = [state, setState];
    $[0] = state;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function _temp(s, newState) {
  return {
    ...s,
    ...newState
  };
}
export const useIsMountedRef = () => {
  const $ = _c(2);
  const isMounted = useRef(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return isMounted;
};
export function useSafeSetState(initialState) {
  const $ = _c(6);
  const [state, setState] = useSetState(initialState);
  const isMounted = useIsMountedRef();
  let t0;
  if ($[0] !== isMounted || $[1] !== setState) {
    t0 = (...t1) => {
      const args = t1;
      return isMounted.current && setState(...args);
    };
    $[0] = isMounted;
    $[1] = setState;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const safeSetState = t0;
  let t1;
  if ($[3] !== safeSetState || $[4] !== state) {
    t1 = [state, safeSetState];
    $[3] = safeSetState;
    $[4] = state;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
export function usePrevious<T>(value: T) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    ref.current = value;
  });
  // eslint-disable-next-line react-hooks/refs -- Intentional render-time ref read to return previous value
  return ref.current;
}
export function useDeepCompareEffect(effect: EffectCallback, dependencies: DependencyList) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Deep comparison wrapper: deps are managed via useDeepCompareMemoize
  useEffect(effect, useDeepCompareMemoize(dependencies));
}
export function useDeepCompareCallback<T extends (...args: any[]) => any>(callback: T, dependencies: DependencyList) {
  // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/use-memo -- Deep comparison wrapper: deps are managed via useDeepCompareMemoize
  return useCallback(callback, useDeepCompareMemoize(dependencies));
}
export const useUpdateEffect = (effect: EffectCallback, deps: DependencyList) => {
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      return effect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Generic effect wrapper: deps are passed dynamically by the caller
  }, deps);
};
export const useDebounce = (t0, t1, t2) => {
  const $ = _c(5);
  const fn = t0 === undefined ? _temp2 : t0;
  const ms = t1 === undefined ? 0 : t1;
  let t3;
  if ($[0] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const deps = t3;
  let t4;
  if ($[2] !== fn || $[3] !== ms) {
    t4 = () => {
      const timeout = setTimeout(fn, ms);
      return () => {
        clearTimeout(timeout);
      };
    };
    $[2] = fn;
    $[3] = ms;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useUpdateEffect(t4, deps);
};
function _temp2() {}